import { ModalContent, ModalFooter } from 'pobble-components/dist/modal'
import React, { useState } from 'react'
import injectSheet from 'react-jss'
import FormLabel from 'pobble-components/dist/form-elements/form-label'
import FormTextArea from 'pobble-components/dist/form-elements/form-text-area'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import FormRadio from 'pobble-components/dist/form-elements/form-radio'
import Button from 'pobble-components/dist/button'
import ButtonElement from 'pobble-components/dist/button-element'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown'
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle'
import Tooltip from 'pobble-components/dist/tooltip'
import {
  useFeatureCounter,
  SubsCrown
} from 'pobble-components/dist/subscriptions'

const FormControl = injectSheet({
  container: {
    position: 'relative',
    '& input': {
      appearance: 'none',
      position: 'absolute',
      backgroundColor: '#fff',
      margin: 0,
      '& + *': {
        height: '100%',
        cursor: 'pointer',
        border: '1px solid #ddd',
        padding: '16px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        position: 'relative',
        '& $checkIcon:first-child': {
          display: 'none'
        }
      },
      '&:checked + *': {
        borderColor: 'var(--blue)',
        background: '#f5fcff',
        '& $checkIcon': {
          color: 'var(--blue)',
          '&:first-child': {
            display: 'block'
          },
          '&:last-child': {
            display: 'none'
          }
        }
      },
      '&:disabled + *': {
        backgroundColor: 'var(--light-gray--)'
      }
    }
  },
  checkIcon: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    color: '#ddd',
    fontSize: '16px'
  }
})(function FormControl({ classes, children, THEME, hideIcon, ...props }) {
  return (
    <label className={classes.container}>
      <input type="radio" {...props} />
      <span className={classes.component}>
        {!hideIcon && (
          <>
            <FontAwesomeIcon
              className={classes.checkIcon}
              icon={faCheckCircle}
            />
            <FontAwesomeIcon className={classes.checkIcon} icon={faCircle} />
          </>
        )}
        {children}
      </span>
    </label>
  )
}, [])

const styles = ({ COLORS }) => ({
  content: {
    padding: '0',
    fontSize: '14px',
    '& section': {
      padding: '2em 1.43em',
      borderTop: '1px solid #ececec',
      alignItems: 'center',
      '&:first-child': {
        border: 'none'
      }
    },
    '& sup': {
      color: COLORS.RED.NORMAL
    }
  },
  commentHint: {
    display: 'none',
    '@media (min-width: 575px)': {
      display: 'inline'
    }
  },
  infoSection: {
    '& > *': {
      marginTop: '2em',
      '&:first-child': {
        marginTop: '0'
      }
    }
  },
  staButton: {
    color: COLORS.TEXT.LIGHT_GREY.NORMAL,
    display: 'flex',
    alignItems: 'center',
    marginTop: '30px',
    '& svg': {
      marginLeft: '6px'
    }
  },
  staChoices: {
    listStyle: 'none',
    padding: '0',
    margin: '0 0 20px',
    display: 'flex',
    '& li': {
      marginRight: '20px'
    },
    '& label': {
      display: 'flex',
      alignItems: 'center',
      '& span': {
        marginRight: '5px'
      }
    }
  },
  staDescription: {
    margin: '20px 0',
    '& a': {
      color: COLORS.TEXT.LIGHT_GREY.NORMAL
    }
  },
  publishChoices: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '16px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
      '& > *': {
        height: '100%'
      }
    }
  },
  publishChoice: {
    color: 'var(--gray--)',
    '& svg': {
      color: 'var(--gray---)',
      margin: '1px 12px 0 0'
    },
    '& > span': {
      display: 'block',
      margin: '4px 0',
      '&:first-child': {
        fontWeight: 'bold',
        margin: '0 0 12px',
        font: 'var(--heading--)',
        color: 'var(--gray-)'
      }
    }
  },
  publishPoint: {
    display: 'flex !important'
  },
  commentSection: {
    marginTop: '24px'
  },
  publishCrown: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    fontSize: '14px'
  }
})

const WorkForm = injectSheet(styles)(
  ({
    classes,
    defaultValues,
    canPublish,
    pupilName,
    pupilId,
    onCancel,
    submitButton,
    onSubmit
  }) => {
    const [publish, setPublish] = useState(false)
    const [comment, setComment] = useState('')
    const [title, setTitle] = useState(defaultValues.title)
    const [tags, setTags] = useState('')
    const [showSta, setShowSta] = useState(false)
    const [sta, setSta] = useState(null)
    const [staExtraInfo, setStaExtraInfo] = useState('')
    const [description, setDescription] = useState(defaultValues.description)
    const workState = publish ? 'published' : 'evidence_bank'

    const [incrementPublishWorkCounter, publishWorkSP] = useFeatureCounter(
      'WorkPublishing',
      sp => sp.publish_work
    )

    return (
      <form
        onSubmit={e => {
          e.preventDefault()
          onSubmit({
            workState,
            title,
            description: description || null,
            comment: comment || null,
            sta:
              !showSta || !sta
                ? null
                : {
                    value: sta,
                    extraInfo: sta !== 'in_part' ? null : staExtraInfo
                  },
            tags: tags
              .split(',')
              .map(tag => tag.trim())
              .filter(tag => !!tag)
          }).then(() => {
            if (workState === 'published') {
              incrementPublishWorkCounter()
            }
          })
        }}>
        <ModalContent className={classes.content}>
          <section className={classes.publishSection}>
            <div className={classes.publishChoices}>
              <FormControl
                checked={!publish}
                onChange={() => setPublish(false)}>
                <span className={classes.publishChoice}>
                  <span>Save to evidence bank</span>
                  <span className={classes.publishPoint}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Saved to pupil's evidence bank and accesible for moderation.
                  </span>
                  <span className={classes.publishPoint}>
                    <FontAwesomeIcon icon={faTimesCircle} />
                    <span>
                      <strong>Not</strong> published on school celebration page.
                    </span>
                  </span>
                </span>
              </FormControl>
              <FormControl
                disabled={!canPublish}
                hideIcon={publishWorkSP.restricted}
                checked={publish}
                onChange={() =>
                  publishWorkSP.restricted
                    ? publishWorkSP.showDialog()
                    : setPublish(true)
                }>
                {publishWorkSP.restricted && (
                  <SubsCrown accent className={classes.publishCrown} />
                )}
                <span className={classes.publishChoice}>
                  <span>Save and publish</span>
                  <span className={classes.publishPoint}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Saved to pupil's evidence bank and accesible for moderation.
                  </span>
                  <span className={classes.publishPoint}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Published on school celebration page.
                  </span>
                </span>
              </FormControl>
            </div>
            {publish && (
              <div className={classes.commentSection}>
                <FormLabel small htmlFor="comment">
                  <span>
                    <strong>
                      Leave a public comment on {pupilName}
                      's work
                    </strong>
                  </span>
                </FormLabel>
                <FormTextArea
                  id="comment"
                  placeholder="e.g. Congratulations on having your work published on Pobble! I particularly liked the way you …"
                  value={comment}
                  autoFocus
                  onChange={e => setComment(e.currentTarget.value)}
                />
              </div>
            )}
          </section>
          <section className={classes.infoSection}>
            <div>
              <FormLabel small htmlFor="title">
                <strong>
                  Title
                  <sup>*</sup>
                </strong>
              </FormLabel>
              <FormInput
                required
                id="title"
                placeholder="e.g. Poetry in the war"
                value={title}
                onChange={e => setTitle(e.currentTarget.value)}
              />
            </div>
            <div>
              <FormLabel small htmlFor="desc">
                <strong>Description</strong>
              </FormLabel>
              <FormTextArea
                id="desc"
                placeholder="e.g. We have written poems about World War II."
                value={description}
                onChange={e => setDescription(e.currentTarget.value)}
              />
            </div>
            <div>
              <FormLabel small htmlFor="tags">
                <strong>Tags</strong>
              </FormLabel>
              <FormInput
                id="tags"
                placeholder="e.g. MightyWrite2022"
                value={tags}
                onChange={e => setTags(e.currentTarget.value)}
              />
            </div>
            <div className={classes.staSection}>
              <ButtonElement
                onClick={() => setShowSta(!showSta)}
                className={classes.staButton}>
                Set STAs independent writing criteria (optional){' '}
                <FontAwesomeIcon icon={showSta ? faCaretUp : faCaretDown} />
              </ButtonElement>
              {showSta && (
                <>
                  <p className={classes.staDescription}>
                    Does this piece meet the STAs independent writing criteria?{' '}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://app.pobble.com/account/files/Independent.Writing.pdf">
                      <Tooltip content="Find out more">
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Tooltip>
                    </a>
                  </p>
                  <ul className={classes.staChoices}>
                    <li>
                      <label>
                        <FormRadio
                          name="sta"
                          checked={sta === 'yes'}
                          onChange={() => setSta('yes')}
                        />
                        Yes
                      </label>
                    </li>
                    <li>
                      <label>
                        <FormRadio
                          name="sta"
                          checked={sta === 'no'}
                          onChange={() => setSta('no')}
                        />
                        No
                      </label>
                    </li>
                    <li>
                      <label>
                        <FormRadio
                          name="sta"
                          checked={sta === 'in_part'}
                          onChange={() => setSta('in_part')}
                        />
                        In part
                      </label>
                    </li>
                  </ul>
                  {sta === 'in_part' && (
                    <FormTextArea
                      required
                      value={staExtraInfo}
                      onChange={e => setStaExtraInfo(e.target.value)}
                      placeholder="e.g. First paragraph shared writing"
                    />
                  )}
                </>
              )}
            </div>
          </section>
        </ModalContent>
        <ModalFooter className={classes.footer} alignRight childrenGap>
          <Button simple tall action={onCancel}>
            Cancel
          </Button>
          {submitButton}
        </ModalFooter>
      </form>
    )
  }
)

export default WorkForm
